import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { getData as apiGetData } from 'api/Api'
import { CancelError } from '@kanbo/api'
import { getPermissionMessageId, PermissionLevels } from 'utils/permission'
import {
  InputGroup,
  FormControl,
  FormGroup,
  ControlLabel
} from 'react-bootstrap'
import Button from 'components/Button'
import User from 'components/User'
import Scrollbars from 'components/Scrollbars'
import KanBoIcon from 'components/KanBoIcon'
import InfiniteScroll from 'components/InfiniteScroll'
import BoardUser from '../User'
import classes from './Users.module.scss'

const FETCH_USERS_LIMIT = 50

export class Users extends React.Component {
  static propTypes = {
    boardId: PropTypes.number.isRequired,
    users: PropTypes.array.isRequired,
    cursor: PropTypes.object,
    onlyActiveCursor: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    loaded: PropTypes.bool.isRequired,
    onlyActiveLoading: PropTypes.bool.isRequired,
    onlyActiveLoaded: PropTypes.bool.isRequired,

    fetchUsers: PropTypes.func.isRequired,
    addUserArray: PropTypes.func.isRequired,

    limit: PropTypes.number,
    onlyActive: PropTypes.bool,
    getUserProps: PropTypes.func,
    className: PropTypes.string,
    renderPermissionGroup: PropTypes.func,
    renderPermissionGroupHeader: PropTypes.func,
    showAllPermissionGroups: PropTypes.bool,
    showPermissionGroups: PropTypes.bool,
    showSearch: PropTypes.bool,
    showAsList: PropTypes.bool,
  }

  static defaultProps = {
    showPermissionGroups: true,
    showSearch: true,
    showAsList: false,
    getUserProps: () => ({})
  }

  static emptyArray = []

  static userStyle = { marginRight: 5, marginBottom: 5 }

  state = {
    searchKeyword: '',
    searchUsers: Users.emptyArray,
    searchCursor: null,
    searchIsLoading: false,
    searchIsLoaded: false,
    showAsList: this.props.showAsList,
  }

  componentWillUnmount () {
    this.cancelFetchUsers()
  }

  isLoading = () =>
    this.state.searchKeyword
      ? this.state.searchIsLoading
      : this.props.onlyActive
        ? this.props.onlyActiveLoading
        : this.props.loading

  isLoaded = () => {
    let isLoaded =
      this.state.searchKeyword
        ? this.state.searchIsLoaded
        : this.props.onlyActive
          ? this.props.onlyActiveLoaded
          : this.props.loaded

    if ( // Get all inactive users that could be omitted in previous users fetches in onlyActive mode
      !this.state.searchKeyword &&
      !this.props.loaded &&
      !this.props.onlyActive &&
      this.props.onlyActiveCursor &&
      (
        !this.props.cursor ||
        (
          this.props.cursor.IdGreaterThan < this.props.onlyActiveCursor.IdGreaterThan ||
          this.props.cursor.PermissionLevel > this.props.onlyActiveCursor.PermissionLevel
        )
      )
    ) {
      isLoaded = false
    }

    const userCount = 
      this.state.searchKeyword
        ? this.state.searchUsers.length
        : this.props.users.length

    if (
      !isLoaded &&
      this.props.limit &&
      userCount >= this.props.limit  
    ) return true

    return isLoaded
  }

  getCursor = () =>
    this.state.searchKeyword
      ? this.state.searchCursor
      : this.props.onlyActive
        ? this.props.onlyActiveCursor
        : this.props.cursor

  getUsers = () => {
    const { users, limit } = this.props
    const { searchKeyword, searchUsers } = this.state

    let _users = searchKeyword
      ? searchUsers
      : users

    return limit
      ? _users.slice(0, limit)
      : _users
  }

  getUsersByGroups = () => {
    const { showAllPermissionGroups } = this.props

    const groups =
      showAllPermissionGroups
        ? [PermissionLevels.OWNER, PermissionLevels.MEMBER, PermissionLevels.VISITOR].reduce((groups, permissionLevel) => {
          groups[permissionLevel] = {
            permissionLevel,
            users: []
          }

          return groups
        }, {})
        : {}

    const groupsByPermissionLevel =
      this.getUsers()
        .reduce((groups, user) => {
          if (!groups[user.permissionLevel]) {
            groups[user.permissionLevel] = {
              permissionLevel: user.permissionLevel,
              users: []
            }
          }

          groups[user.permissionLevel].users.push(user)

          return groups
        }, groups)

    return Object.keys(groupsByPermissionLevel)
      .map(key => groupsByPermissionLevel[key])
      .sort((a, b) => b.permissionLevel - a.permissionLevel)
  }

  cancelFetchUsers = () => {
    if (this.fetchUsersPromise && this.fetchUsersPromise.cancel) {
      this.fetchUsersPromise.cancel()
      delete this.fetchUsersPromise
    }
  }

  fetchUsers = ({ limit = FETCH_USERS_LIMIT } = {}) => {
    const { boardId, onlyActive, fetchUsers, addUserArray } = this.props
    const { searchKeyword } = this.state

    if (!searchKeyword) {
      fetchUsers({
        boardId,
        cursor: this.getCursor(),
        onlyActive,
        limit
      })
    } else {
      this.cancelFetchUsers()

      this.setState({
        searchIsLoading: true
      })

      const args = {
        keyword: searchKeyword,
        boardId,
        limit,
        cursor: this.getCursor()
      }

      if (onlyActive) {
        args.active = true
      }

      this.fetchUsersPromise = apiGetData('BoardUsers', args)
      this.fetchUsersPromise.then(({ Users, Cursor }) => {
        this.setState((prevState) => {
          const searchUsers = [
            ...prevState.searchUsers,
            ...Users.map(User => ({
              id: User.Id,
              permissionLevel: User.PermissionLevel
            }))
          ]

          addUserArray({
            boardId,
            array: Users.map(User => ({
              id: User.Id,
              name: User.Name,
              email: User.Email,
              profileKey: User.ProfileKey,
              licenseType: User.LicenseType,
              isActive: User.Active,
              isLoaded: true
            }))
          })

          return {
            searchUsers,
            searchCursor: Cursor,
            searchIsLoading: false,
            searchIsLoaded: !Cursor
          }
        })
      }).catch((error) => {
        if (!(error instanceof CancelError)) {
          throw error
        }
      })
    }
  }

  handleSearchChange = (event) => {
    this.setState({
      searchKeyword: event.target.value,
      searchUsers: Users.emptyArray,
      searchCursor: null,
      searchIsLoading: false,
      searchIsLoaded: false
    })
  }

  handleDisplayModeClick = () => {
    this.setState({
      showAsList: !this.state.showAsList
    })
  }

  renderUsers = (users) => {
    const {
      getUserProps
    } = this.props
    const {
      showAsList
    } = this.state

    const props = showAsList ? {
      listItem: true,
      defaultMenu: true,
      presence: true
    } : {
      avatar: true,
      defaultMenu: true,
      presence: true,
      style: Users.userStyle
    }

    return users.map(user =>
      <BoardUser
        {...props}
        key={user.id}
        id={user.id}
      >
        {(userProps) =>
          <User
            {...userProps}
            {...getUserProps(userProps)}
          />
        }
      </BoardUser>
    )
  }

  renderPermissionGroup = (group) => {
    const {
      renderPermissionGroupHeader,
      showPermissionGroups
    } = this.props
    const { showAsList } = this.state

    if (!showPermissionGroups) {
      return this.renderUsers(group.users)
    }

    const permissionGroupHeader = renderPermissionGroupHeader
      ? renderPermissionGroupHeader({ permissionLevel: group.permissionLevel })
      : null

    if (!permissionGroupHeader && group.users.length === 0) return this.renderUsers(group.users)

    return (
      <FormGroup
        key={group.permissionLevel}
      >
        <ControlLabel className={classes.content}>
          <FormattedMessage id={getPermissionMessageId(group.permissionLevel)} />
        </ControlLabel>
        {permissionGroupHeader}
        <ul className={'list-group' + (!showAsList ? ` ${classes.content}` : '')}>
          {this.renderUsers(group.users)}
        </ul>
      </FormGroup>
    )
  }

  render () {
    const {
      children,
      className,
      showPermissionGroups,
      showSearch,
      renderPermissionGroup,
    } = this.props
    const {
      searchKeyword,
      showAsList
    } = this.state

    if (typeof children === 'function') {
      return children({
        ...this.props,
        isLoading: this.isLoading(),
        isLoaded: this.isLoaded(),
        fetchUsers: this.fetchUsers,
      })
    }

    let compClassName = classes.users
    if (className) compClassName += ' ' + className

    return (
      <div className={compClassName}>
        {showSearch &&
          <div className={classes.searchInputWrapper}>
            <InputGroup>
              <FormattedMessage id='USERS_SEARCH_HINT'>
                {formattedMessage =>
                  <FormControl
                    type='text'
                    placeholder={formattedMessage}
                    value={searchKeyword}
                    onChange={this.handleSearchChange}
                  />
                }
              </FormattedMessage>
              <InputGroup.Button>
                <Button
                  icon={<KanBoIcon fixedWidth name={showAsList ? 'kanbo-16-list-view' : 'kanbo-16-thumbnails-view'} />}
                  onClick={this.handleDisplayModeClick}
                />
              </InputGroup.Button>
            </InputGroup>
          </div>
        }
        <Scrollbars
          className={classes.userContainer}
        >
          <InfiniteScroll
            key={searchKeyword}
            isLoading={this.isLoading()}
            isLoaded={this.isLoaded()}
            onLoadMore={this.fetchUsers}
          >
            <div className={(!showPermissionGroups && !showAsList ? classes.content : null)}>
              {this.getUsersByGroups().map(group =>
                renderPermissionGroup
                  ? renderPermissionGroup(group, this.renderPermissionGroup)
                  : this.renderPermissionGroup(group)
              )}
            </div>
          </InfiniteScroll>
        </Scrollbars>
      </div>
    )
  }
}

export default Users
